// Copyright 2025. WebPros International GmbH. All rights reserved.

import {
    Heading,
    List,
    Translate,
} from '@plesk/ui-library';
import {
    ReactNode,
    useMemo,
} from 'react';
import { StyledTable } from 'common/components/styles/StyledTable';
import { IComputeResourceRecoveryReadinessStatusResponse } from 'common/api/resources/DisasterRecovery';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';
import CheckCrossIcon from 'admin/disasterRecovery/components/CheckCrossIcon/CheckCrossIcon';

export default function AvailableResourcesItem({
    title,
    requireResources,
    availableResources,
}: {
    title: ReactNode;
    requireResources?: IComputeResourceRecoveryReadinessStatusResponse['require_resources']['for_all_vms'];
    availableResources?: IComputeResourceRecoveryReadinessStatusResponse['available_resources'];
}) {

    const columns = useMemo(() => [
        {
            key: 'resource',
            title: <Translate content={'disasterRecovery.readinessStatus.availableResource.table.resource'} />,
            width: '40%',
        },
        {
            key: 'status',
            title: <Translate content={'disasterRecovery.readinessStatus.availableResource.table.status'} />,
            align: 'center' as const,
            width: '20%',
        },
        {
            key: 'required',
            title: <Translate content={'disasterRecovery.readinessStatus.availableResource.table.required'} />,
            align: 'right' as const,
            width: '20%',
        },
        {
            key: 'available',
            title: <Translate content={'disasterRecovery.readinessStatus.availableResource.table.available'} />,
            align: 'right' as const,
            width: '20%',
        },
    ], []);

    const data = useMemo(() => [
        {
            resource: 'RAM',
            status: <CheckCrossIcon value={(!!availableResources && availableResources.is_ram_unlimited) || (!!requireResources && !!availableResources && requireResources.ram <= availableResources.ram)} />,
            required: requireResources?.ram ? `${convertToDataUnit(requireResources.ram, DataUnit.GiB)} GiB` : 0,
            available: availableResources?.is_ram_unlimited
                ? <Translate content="disasterRecovery.readinessStatus.availableResource.unlimited" />
                : (availableResources?.ram ? `${convertToDataUnit(availableResources.ram, DataUnit.GiB)} GiB` : 0),
        },
        {
            resource: 'VCPU',
            status: <CheckCrossIcon value={(!!availableResources && availableResources.is_vcpu_unlimited) || (!!requireResources && !!availableResources && requireResources.vcpu <= availableResources.vcpu)} />,
            required: requireResources?.vcpu ?? 0,
            available: availableResources?.is_vcpu_unlimited
                ? <Translate content="disasterRecovery.readinessStatus.availableResource.unlimited" />
                : (availableResources?.vcpu ?? 0),
        },
        {
            resource: 'Disk',
            status: <CheckCrossIcon value={(!!availableResources && availableResources.is_disk_unlimited) || (!!requireResources && !!availableResources && requireResources.disk <= availableResources.disk)} />,
            required: requireResources?.disk ? `${availableResources?.disk} GiB` : 0,
            available: availableResources?.is_disk_unlimited
                ? <Translate content="disasterRecovery.readinessStatus.availableResource.unlimited" />
                : (availableResources?.disk ? `${availableResources?.disk} GiB` : 0),
        },
    ], [requireResources, availableResources]);

    return (
        <div style={{ marginBottom: 16 }}>
            <Heading level={4}>
                {title}
            </Heading>

            <StyledTable style={{ width: '50%' }}>
                <List
                    columns={columns}
                    data={data}
                    rowKey={'resource'}
                />
            </StyledTable>
        </div>
    );
}
